import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Common options for html-to-image
const getImageOptions = (width = 1920, height = 1080) => ({
  quality: 1.0,
  pixelRatio: 2,
  width,
  height,
  style: { 
    transform: 'none',
    margin: 0,
    padding: 0,
  },
  cacheBust: true,
  skipFonts: false,
  includeQueryParams: true,
  // Use CORS proxy for external images
  imagePlaceholder: undefined,
  // Skip external images that fail CORS
  filter: (node) => {
    // Always include the node
    return true;
  },
});

// Wait for images to load in an element
const waitForImages = async (element) => {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve; // Continue even if image fails
      // Add crossOrigin for external images
      if (img.src && img.src.startsWith('http')) {
        img.crossOrigin = 'anonymous';
      }
    });
  });
  await Promise.all(promises);
  // Additional wait for rendering
  await new Promise(resolve => setTimeout(resolve, 500));
};

export const exportToPdf = async (slideElements, filename = 'pitch-deck.pdf') => {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [1920, 1080],
  });

  for (let i = 0; i < slideElements.length; i++) {
    try {
      await waitForImages(slideElements[i]);
      
      const dataUrl = await toPng(slideElements[i], getImageOptions());
      
      if (i > 0) pdf.addPage();
      pdf.addImage(dataUrl, 'PNG', 0, 0, 1920, 1080);
    } catch (error) {
      console.error(`Error exporting slide ${i + 1}:`, error);
      // Create a placeholder for failed slides
      if (i > 0) pdf.addPage();
      pdf.setFontSize(48);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Slide ${i + 1} - Export Error`, 960, 540, { align: 'center' });
    }
  }

  pdf.save(filename);
};

export const exportToPptx = async (slideElements, filename = 'pitch-deck.pptx') => {
  if (!window.PptxGenJS) {
    alert("PPTX Generator not loaded");
    return;
  }
  const pptx = new window.PptxGenJS();
  pptx.layout = 'LAYOUT_16x9';

  for (let i = 0; i < slideElements.length; i++) {
    try {
      await waitForImages(slideElements[i]);
      
      const dataUrl = await toPng(slideElements[i], getImageOptions());
      
      const slide = pptx.addSlide();
      slide.addImage({ data: dataUrl, x: 0, y: 0, w: '100%', h: '100%' });
    } catch (error) {
      console.error(`Error exporting slide ${i + 1}:`, error);
      // Add placeholder slide
      const slide = pptx.addSlide();
      slide.addText(`Slide ${i + 1} - Export Error`, { x: 1, y: 2.5, w: 8, h: 1, fontSize: 24, color: '808080', align: 'center' });
    }
  }

  pptx.writeFile({ fileName: filename });
};

export const exportToZip = async (slideElements, filename = 'pitch-deck-images.zip') => {
  const zip = new JSZip();
  const folder = zip.folder('slides');

  for (let i = 0; i < slideElements.length; i++) {
    try {
      await waitForImages(slideElements[i]);
      
      const dataUrl = await toPng(slideElements[i], getImageOptions());
      
      const base64Data = dataUrl.replace(/^data:image\/png;base64,/, "");
      folder.file(`slide-${i + 1}.png`, base64Data, { base64: true });
    } catch (error) {
      console.error(`Error exporting slide ${i + 1}:`, error);
    }
  }

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, filename);
};

// Export single slide as PNG
export const exportSingleToPng = async (element, filename) => {
  try {
    await waitForImages(element);
    
    const dataUrl = await toPng(element, getImageOptions());
    
    saveAs(dataUrl, filename);
  } catch (error) {
    console.error('Error exporting PNG:', error);
    throw error;
  }
};
